import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ThumbsUp, ThumbsDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const storageKey = (slug) => `verifaeye_guide_vote_${slug}`;

export default function GuidePoll({ slug }) {
  const [state, setState] = useState('idle'); // idle | submitting | done
  const [tally, setTally] = useState(null); // {yes, no} once known
  const [ownVote, setOwnVote] = useState(null); // true | false

  useEffect(() => {
    // Skip the poll entirely if this browser already voted
    const cached = localStorage.getItem(storageKey(slug));
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        setOwnVote(parsed.helpful);
        setTally(parsed.tally || null);
        setState('done');
      } catch {
        localStorage.removeItem(storageKey(slug));
      }
    }
  }, [slug]);

  const submit = async (helpful) => {
    if (state === 'submitting') return;
    setState('submitting');
    try {
      const res = await axios.post(`${API}/api/guides/feedback`, { slug, helpful });
      const t = { yes: res.data.yes, no: res.data.no };
      setTally(t);
      setOwnVote(helpful);
      setState('done');
      localStorage.setItem(storageKey(slug), JSON.stringify({ helpful, tally: t }));
    } catch (err) {
      // Network or backend hiccup — let the user retry
      setState('idle');
    }
  };

  if (state === 'done') {
    return (
      <section
        className="mt-16 rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center"
        data-testid="guide-poll-done"
      >
        <p className="text-lg font-medium text-slate-900">Thanks for the feedback.</p>
        <p className="mt-2 text-slate-600 text-sm">
          {ownVote
            ? "Glad this was useful — more guides like this on the way."
            : "Noted. We use this to sharpen the guides that aren't landing."}
        </p>
        {tally && (tally.yes + tally.no > 0) && (
          <p className="mt-4 text-xs text-slate-500" data-testid="guide-poll-tally">
            {tally.yes} of {tally.yes + tally.no} readers found this useful.
          </p>
        )}
      </section>
    );
  }

  return (
    <section
      className="mt-16 rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center"
      data-testid="guide-poll"
    >
      <p className="text-lg font-medium text-slate-900">Was this useful?</p>
      <p className="mt-2 text-sm text-slate-600">
        A quick tap helps us learn which guides are actually landing.
      </p>
      <div className="mt-5 flex items-center justify-center gap-3">
        <button
          type="button"
          onClick={() => submit(true)}
          disabled={state === 'submitting'}
          data-testid="guide-poll-yes"
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 hover:border-emerald-400 hover:text-emerald-600 transition disabled:opacity-60"
        >
          <ThumbsUp className="w-4 h-4" /> Yes
        </button>
        <button
          type="button"
          onClick={() => submit(false)}
          disabled={state === 'submitting'}
          data-testid="guide-poll-no"
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 hover:border-rose-400 hover:text-rose-600 transition disabled:opacity-60"
        >
          <ThumbsDown className="w-4 h-4" /> Not really
        </button>
      </div>
    </section>
  );
}
